type FilterLike = {
  field: string;
  operator: string;
  value: unknown;
  not?: boolean;
};

interface ExtractAdObjectTypeResult<TFilter extends FilterLike> {
  filters: TFilter[];
  types: string[];
  negated: boolean;
}

export const AD_TYPE_TO_CLASS: Record<string, string> = {
  ADComputer: 'computer',
  ADDomain: 'domain',
  ADUser: 'user',
  ADGroup: 'group',
  ADContainer: 'container',
  ADCertTemplate: 'certtemplate',
  ADAIACA: 'aiaca',
};

export const extractAdObjectTypeFilters = <TFilter extends FilterLike>(
  filters: TFilter[]
): ExtractAdObjectTypeResult<TFilter> => {
  const types = new Set<string>();
  let negated = false;

  const visit = (filter: TFilter): TFilter | null => {
    if (!filter) {
      return null;
    }

    if (filter.field === 'adObjectType') {
      const { value, not } = filter;

      if (Array.isArray(value)) {
        value.forEach(type => {
          if (
            type !== undefined &&
            type !== null &&
            String(type).trim() !== ''
          ) {
            types.add(String(type));
          }
        });
      } else if (value !== undefined && value !== null) {
        const stringValue = String(value);
        if (stringValue.trim() !== '') {
          types.add(stringValue);
        }
      }

      if (not) {
        negated = true;
      }

      return null;
    }

    const maybeChildren = filter.value as TFilter[] | undefined;

    if (
      (filter.operator === 'OR' || filter.operator === 'AND') &&
      Array.isArray(maybeChildren)
    ) {
      const childFilters = maybeChildren
        .map(visit)
        .filter((child): child is TFilter => child !== null);

      if (childFilters.length === 0) {
        return null;
      }

      return {
        ...filter,
        value: childFilters,
      } as TFilter;
    }

    return filter;
  };

  const cleanedFilters = filters
    .map(visit)
    .filter((filter): filter is TFilter => filter !== null);

  return {
    filters: cleanedFilters,
    types: Array.from(types),
    negated,
  };
};

const normalizeAdTypes = (adObjectTypes: string[]): string[] =>
  Array.from(
    new Set(
      adObjectTypes
        .map(type => (type ?? '').toString().trim())
        .filter((type): type is string => type.length > 0)
    )
  );

const mapToClassValue = (type: string): string =>
  AD_TYPE_TO_CLASS[type] || type.toLowerCase().replace(/^ad/, '');

export const buildClassFilterFromAdTypes = <TFilter extends FilterLike>(
  adObjectTypes: string[],
  isNegated: boolean,
  adapt?: (filter: FilterLike) => TFilter
): TFilter | null => {
  const filteredTypes = normalizeAdTypes(adObjectTypes);

  if (filteredTypes.length === 0) {
    return null;
  }

  const classValues = filteredTypes.map(mapToClassValue);
  const toFilter = adapt ?? ((filter: FilterLike) => filter as TFilter);

  if (classValues.length === 1) {
    return toFilter({
      field: 'class',
      operator: '=',
      value: classValues[0],
      ...(isNegated ? { not: true } : {}),
    });
  }

  const childFilters = classValues.map(value =>
    toFilter({
      field: 'class',
      operator: '=',
      value,
      ...(isNegated ? { not: true } : {}),
    })
  );

  return toFilter({
    field: '',
    operator: isNegated ? 'AND' : 'OR',
    value: childFilters,
  });
};
